// Invite e-mail templates stored in the inviter's OWN wallet storage.
//
// The admin keeps HTML templates in the dashboard Explorer under
// /email-templates (file name = template id). The storage is wallet-owned and
// client-side encrypted, so the server can never read it: the dashboard lists
// the folder, downloads + decrypts the chosen file, and sends the HTML inline
// with the invite request. The orchestrator only validates and renders it.
//
// Placeholder set ({{...}} is substituted server-side, HTML-escaped):
//   {{to_name}}              invitee display name ("there" when unknown)
//   {{to_email}}             invitee e-mail address
//   {{link}}                 the personal magic link (REQUIRED in a template)
//   {{email_subject}}        generated subject line
//   {{valid_from}}           window start, inviter's timezone
//   {{valid_until}}          join deadline, inviter's timezone
//   {{valid_date}}           date only, e.g. "7 August 2026"
//   {{valid_from_time}}      start time only, e.g. "3:00 PM"
//   {{valid_until_time}}     join-deadline time (with date when multi-day)
//   {{access_level}}         "Telefon, Chat, VideoChat" or "Telefon, Chat, VideoChat, Datenaustausch"
//   {{calendar_google_url}}  add-to-Google-Calendar URL
//   {{calendar_outlook_url}} add-to-Outlook URL
import { getFilesList, readFile, writeFile } from "../utils/dwclient/dwstorage";
// The Explorer stores user files under the "/User Files" root (the graph's
// root filesystem node), so a folder created there lives at
// "/User Files/email-templates" in storage. The bare "/email-templates" path
// is kept as a legacy fallback for templates saved before this fix.
export const INVITE_TEMPLATE_FOLDER = "/User Files/email-templates";
export const SAMPLE_TEMPLATE_NAME = "dwinity-link-invite.html";
const EMPTY_STORAGE_PROPS = {
    portalClientId: undefined,
    clientSessionToken: undefined,
    ed25519UserShare: undefined,
    loaded: false,
};
// Folders probed directly before falling back to a full-tree scan. These are
// the locations the Explorer actually uses for a user-created
// "email-templates" folder (see INVITE_TEMPLATE_FOLDER above).
const CANDIDATE_TEMPLATE_FOLDERS = [
    INVITE_TEMPLATE_FOLDER, // "/User Files/email-templates" (current Explorer root)
    "/email-templates", // legacy pre-fix location
];
// Rename resilience: once templates have been found, the resolved folder and
// the template file names are remembered (localStorage). If the admin later
// renames/moves the folder in the Explorer, the remembered folder is probed
// directly and — failing that — the full-tree fallback re-locates the
// templates by their remembered file names, so they do not silently vanish
// from the invite dropdown.
const REMEMBERED_FOLDER_KEY = "dwinity:inviteTemplateFolder";
const REMEMBERED_NAMES_KEY = "dwinity:inviteTemplateNames";
function readRemembered(key) {
    try {
        return window.localStorage.getItem(key);
    }
    catch {
        return null; // storage unavailable (private mode etc.) — degrade silently
    }
}
function writeRemembered(key, value) {
    try {
        if (value === null)
            window.localStorage.removeItem(key);
        else
            window.localStorage.setItem(key, value);
    }
    catch {
        /* storage unavailable — resilience degrades, listing still works */
    }
}
/** Folder the templates were last found in — this is what the invite panel
 *  shows and links to, so after a rename the UI points at the real location
 *  instead of insisting on the default path. */
export function getResolvedTemplateFolder() {
    return readRemembered(REMEMBERED_FOLDER_KEY) || INVITE_TEMPLATE_FOLDER;
}
/** Parent folder of a template file path ("/a/b/x.html" → "/a/b"). */
function folderOf(path) {
    const i = path.replace(/\/+$/, "").lastIndexOf("/");
    return i > 0 ? path.slice(0, i) : "/";
}
/** Persist where the templates actually live (+ their names) so a later
 *  Explorer rename/move can be recovered from. */
function rememberTemplates(list) {
    if (list.length === 0)
        return; // keep the last known-good location
    writeRemembered(REMEMBERED_FOLDER_KEY, folderOf(list[0].path));
    writeRemembered(REMEMBERED_NAMES_KEY, JSON.stringify(list.map((t) => t.name)));
}
// Short-lived cache so re-opening the invite panel (or switching between the
// dropdown and the preview) does not hit the storage API again. The TTL is
// deliberately small: a template saved in the Explorer shows up on the next
// panel open after it expires.
const TEMPLATE_LIST_TTL_MS = 30_000;
let templateListCache = null;
/** Drop the cached template list (used after writing a template). */
export function invalidateInviteTemplateCache() {
    templateListCache = null;
}
/** Pick every .html file inside an "email-templates" folder from a listing.
 *  `baseFolder` is set when the listing was made on a template folder
 *  directly — the API may return paths relative to the listed folder, so
 *  entries without the folder prefix are resolved against it. */
function collectTemplates(byName, entries, baseFolder) {
    const base = baseFolder ? baseFolder.replace(/\/+$/, "") : undefined;
    for (const e of entries || []) {
        if (!e.isFile || !/\.html?$/i.test(e.path))
            continue;
        let path = e.path;
        if (base) {
            // Trusted folder listing: every .html inside belongs to the templates.
            // The API may answer with folder-relative paths — resolve those.
            if (!path.startsWith(`${base}/`) && path !== base) {
                path = `${base}/${path.replace(/^\/+/, "")}`;
            }
        }
        else if (!/(^|\/)email-templates\//i.test(path)) {
            continue; // full-tree scan: only folders still named "email-templates"
        }
        const name = path.replace(/\/+$/, "").split("/").pop() || path;
        if (!byName.has(name))
            byName.set(name, { name, path, size: e.size });
    }
}
/** Rename recovery on a full-tree listing: locate the folder that now holds
 *  the previously remembered template files (matched by file name) and treat
 *  ALL .html files in it as the template folder's content. */
function recoverRenamedFolder(byName, entries) {
    let remembered;
    try {
        remembered = JSON.parse(readRemembered(REMEMBERED_NAMES_KEY) || "[]");
    }
    catch {
        remembered = [];
    }
    if (!Array.isArray(remembered) || remembered.length === 0)
        return;
    const rememberedSet = new Set(remembered.map((n) => String(n).toLowerCase()));
    // Count remembered-name hits per folder, pick the folder with the most.
    const hits = new Map();
    for (const e of entries || []) {
        if (!e.isFile || !/\.html?$/i.test(e.path))
            continue;
        const name = e.path.replace(/\/+$/, "").split("/").pop() || "";
        if (rememberedSet.has(name.toLowerCase())) {
            const folder = folderOf(e.path);
            hits.set(folder, (hits.get(folder) || 0) + 1);
        }
    }
    let best = null;
    let bestHits = 0;
    for (const [folder, n] of hits) {
        if (n > bestHits) {
            best = folder;
            bestHits = n;
        }
    }
    if (!best)
        return;
    const folder = best;
    collectTemplates(byName, (entries || []).filter((e) => e.isFile && folderOf(e.path) === folder), folder);
}
/** List the HTML templates in the wallet's /email-templates folder.
 *  A missing folder is simply an empty list — the default template remains. */
export async function listInviteTemplates() {
    if (templateListCache && Date.now() - templateListCache.at < TEMPLATE_LIST_TTL_MS) {
        return templateListCache.list;
    }
    const byName = new Map();
    // Fast path: probe the known template folders directly (recursively — the
    // folders themselves are small) instead of walking the whole storage tree.
    // The folder templates were last found in (possibly renamed/moved via the
    // Explorer) is probed too. A missing folder answers with an error; that is
    // expected, not a failure.
    const rememberedFolder = readRemembered(REMEMBERED_FOLDER_KEY);
    const probeFolders = [...CANDIDATE_TEMPLATE_FOLDERS];
    if (rememberedFolder && !probeFolders.includes(rememberedFolder))
        probeFolders.push(rememberedFolder);
    let anyCandidateFound = false;
    await Promise.all(probeFolders.map(async (folder) => {
        try {
            const res = await getFilesList(EMPTY_STORAGE_PROPS, folder, true);
            // A successful listing means the folder exists — even when empty —
            // so the expensive full-tree fallback can be skipped.
            anyCandidateFound = true;
            collectTemplates(byName, res?.entries, folder);
        }
        catch {
            // Folder does not exist at this location — try the others / fallback.
        }
    }));
    // Fallback: scan the full tree whenever the fast path found nothing — the
    // Explorer may have placed "email-templates" somewhere unexpected (e.g.
    // "/User Files/Uploaded/email-templates"), or renamed/moved the folder.
    // A merely EXISTING but empty candidate folder must not suppress this
    // fallback (regression: templates under a variable prefix vanished from
    // the dropdown). With the common paths populated this expensive call is
    // skipped entirely.
    if (!anyCandidateFound || byName.size === 0) {
        try {
            const res = await getFilesList(EMPTY_STORAGE_PROPS, "/", true);
            collectTemplates(byName, res?.entries);
            // Nothing named "email-templates" anywhere: recover a renamed/moved
            // folder by locating the previously remembered template file names.
            if (byName.size === 0)
                recoverRenamedFolder(byName, res?.entries);
        }
        catch {
            // Empty/first-run storage — the normal first-run state, not a failure.
            if (!anyCandidateFound)
                return [];
        }
    }
    const list = [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
    rememberTemplates(list);
    templateListCache = { at: Date.now(), list };
    return list;
}
/** Download + decrypt one template file and return its HTML text. */
export async function readInviteTemplate(provider, template) {
    const file = await readFile(provider, EMPTY_STORAGE_PROPS, template.path, template.size);
    return await file.text();
}
/** Quick client-side sanity check mirroring the server's preflight: a
 *  template without the {{link}} placeholder would mail an invite the guest
 *  cannot act on. Returns an error code or null. */
export function checkTemplateHtml(html) {
    if (!html || html.trim() === "")
        return "empty";
    if (!/\{\{\s*link\s*\}\}/.test(html))
        return "missing_link";
    return null;
}
function escapeHtml(v) {
    return String(v)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}
/** Client-side mirror of the orchestrator's renderTemplate (see
 *  upload-orchestrator/src/services/inviteEmail.js): {{param}} placeholders
 *  are substituted with HTML-escaped values; unknown placeholders render as
 *  an empty string rather than leaking raw braces. Used for the pre-send
 *  preview so what the admin sees matches what the server would send. */
export function renderTemplatePreview(html, params) {
    return html.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => Object.prototype.hasOwnProperty.call(params, key) ? escapeHtml(params[key]) : "");
}
/** Sample values for the documented placeholder set — used by the preview so
 *  the rendered mail shows realistic content instead of empty gaps. The
 *  window mirrors the invite form's values when provided. */
export function buildSamplePreviewParams(opts) {
    const from = opts?.validFromIso && !isNaN(Date.parse(opts.validFromIso)) ? new Date(opts.validFromIso) : new Date();
    const until = opts?.joinUntilIso && !isNaN(Date.parse(opts.joinUntilIso))
        ? new Date(opts.joinUntilIso)
        : new Date(from.getTime() + 15 * 60 * 1000);
    const dateEn = from.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    const untilDateEn = until.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    const timeEn = (d) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    const fmt = (d) => d.toLocaleString("hu-HU", { dateStyle: "medium", timeStyle: "short" });
    return {
        to_name: "Alex Example",
        to_email: "alex@example.com",
        link: `${window.location.origin}/guest?token=PREVIEW-SAMPLE-TOKEN`,
        email_subject: `Personal invitation to the secure Dwinity Chat and Video Call – ${dateEn}`,
        valid_from: fmt(from),
        valid_until: fmt(until),
        valid_date: dateEn,
        valid_from_time: timeEn(from),
        valid_until_time: dateEn === untilDateEn ? timeEn(until) : `${timeEn(until)} on ${untilDateEn}`,
        access_level: opts?.scope === "full" ? "Telefon, Chat, VideoChat, Datenaustausch" : "Telefon, Chat, VideoChat",
        calendar_google_url: "https://calendar.google.com/calendar/render?action=TEMPLATE",
        calendar_outlook_url: "https://outlook.live.com/calendar/0/action/compose?rru=addevent",
    };
}
/** The canonical Dwinity Link template, offered on first use when the
 *  template folder is empty. The placeholder contract is documented above. */
export const SAMPLE_TEMPLATE_HTML = `<div style="margin:0;padding:0;background-color:#f4f5f7;">
  <div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;">
    A trusted party invited you to a secure Dwinity Chat and Video Call.
  </div>

  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"
         style="width:100%;margin:0;padding:0;background-color:#f4f5f7;">
    <tr>
      <td align="center" style="padding:24px 12px;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0"
               style="width:100%;max-width:600px;background-color:#ffffff;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;font-family:Arial,Helvetica,sans-serif;">

          <!-- Header -->
          <tr>
            <td style="padding:24px 32px;background-color:#111827;">
              <span style="color:#ffffff;font-size:24px;font-weight:700;letter-spacing:0.5px;">
                dwinity
              </span>
            </td>
          </tr>

          <!-- Invitation -->
          <tr>
            <td style="padding:32px 32px 8px 32px;">
              <p style="margin:0 0 8px 0;color:#2563eb;font-size:13px;font-weight:700;line-height:1.5;text-transform:uppercase;letter-spacing:0.8px;">
                Personal invitation
              </p>
              <h1 style="margin:0 0 20px 0;color:#111827;font-size:26px;line-height:1.25;font-weight:700;">
                Dwinity Link
              </h1>

              <p style="margin:0 0 20px 0;color:#374151;font-size:16px;line-height:1.6;">
                A trusted party invited you to join a secure Dwinity Chat and Video Call:
              </p>

              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"
                     style="width:100%;margin:0 0 22px 0;background-color:#f8fafc;border:1px solid #dbe3ee;border-radius:8px;">
                <tr>
                  <td style="padding:18px 20px;color:#111827;font-size:16px;line-height:1.6;">
                    <strong>{{valid_date}}</strong><br>
                    from <strong>{{valid_from_time}}</strong> to <strong>{{valid_until_time}}</strong>
                  </td>
                </tr>
              </table>

              <p style="margin:0 0 24px 0;color:#4b5563;font-size:14px;line-height:1.65;">
                Your invitation link is personal, valid for one-time use only, and exclusively
                valid within the time window specified above. No registration or app installation
                is required &mdash; simply click the link in your browser.
              </p>

              <!-- Primary action -->
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"
                     style="width:100%;margin:0 0 20px 0;">
                <tr>
                  <td align="center">
                    <a href="{{link}}"
                       style="display:inline-block;min-width:180px;padding:14px 28px;background-color:#2563eb;border:1px solid #2563eb;border-radius:8px;color:#ffffff;font-size:16px;font-weight:700;line-height:1.2;text-align:center;text-decoration:none;">
                      Join now
                    </a>
                  </td>
                </tr>
              </table>

              <p style="margin:0 0 8px 0;color:#6b7280;font-size:13px;line-height:1.5;">
                Or use the following link:
              </p>
              <p style="margin:0 0 26px 0;font-size:13px;line-height:1.55;word-break:break-all;overflow-wrap:anywhere;">
                <a href="{{link}}" style="color:#2563eb;text-decoration:underline;">{{link}}</a>
              </p>

              <!-- Calendar links -->
              <p style="margin:0 0 12px 0;color:#111827;font-size:15px;font-weight:700;line-height:1.5;">
                Add the appointment to your calendar
              </p>
              <p style="margin:0 0 16px 0;color:#6b7280;font-size:13px;line-height:1.5;">
                The calendar entry includes your personal invitation link.
              </p>

              <table role="presentation" cellpadding="0" cellspacing="0" border="0"
                     style="margin:0 0 28px 0;">
                <tr>
                  <td style="padding:0 8px 8px 0;">
                    <a href="{{calendar_google_url}}"
                       style="display:inline-block;padding:10px 16px;border:1px solid #2563eb;border-radius:7px;color:#2563eb;font-size:14px;font-weight:700;text-decoration:none;">
                      + Google Calendar
                    </a>
                  </td>
                  <td style="padding:0 0 8px 0;">
                    <a href="{{calendar_outlook_url}}"
                       style="display:inline-block;padding:10px 16px;border:1px solid #2563eb;border-radius:7px;color:#2563eb;font-size:14px;font-weight:700;text-decoration:none;">
                      + Outlook Calendar
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Why Dwinity -->
          <tr>
            <td style="padding:28px 32px;background-color:#f8fafc;border-top:1px solid #e5e7eb;">
              <h2 style="margin:0 0 20px 0;color:#111827;font-size:21px;line-height:1.3;font-weight:700;">
                Why Dwinity Link?
              </h2>

              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"
                     style="width:100%;">
                <tr>
                  <td valign="top" width="22" style="padding:0 8px 15px 0;color:#2563eb;font-size:18px;line-height:1.4;">&#10003;</td>
                  <td style="padding:0 0 15px 0;color:#4b5563;font-size:14px;line-height:1.6;">
                    <strong style="color:#111827;">No account, no password</strong> &ndash;
                    You enter the room via a link, with no registration. Other platforms require
                    an account for full functionality.
                  </td>
                </tr>
                <tr>
                  <td valign="top" width="22" style="padding:0 8px 15px 0;color:#2563eb;font-size:18px;line-height:1.4;">&#10003;</td>
                  <td style="padding:0 0 15px 0;color:#4b5563;font-size:14px;line-height:1.6;">
                    <strong style="color:#111827;">The operator cannot read your content</strong> &ndash;
                    Encryption keys are generated on your device and never leave it. Elsewhere,
                    keys are generated and managed server-side by default.
                  </td>
                </tr>
                <tr>
                  <td valign="top" width="22" style="padding:0 8px 15px 0;color:#2563eb;font-size:18px;line-height:1.4;">&#10003;</td>
                  <td style="padding:0 0 15px 0;color:#4b5563;font-size:14px;line-height:1.6;">
                    <strong style="color:#111827;">No stored history</strong> &ndash;
                    The server does not store chats, files, or calls. Other platforms keep your
                    data server-side.
                  </td>
                </tr>
                <tr>
                  <td valign="top" width="22" style="padding:0 8px 15px 0;color:#2563eb;font-size:18px;line-height:1.4;">&#10003;</td>
                  <td style="padding:0 0 15px 0;color:#4b5563;font-size:14px;line-height:1.6;">
                    <strong style="color:#111827;">E2E for everything</strong> &ndash;
                    Chat, files, audio, and video are always protected. On mainstream platforms,
                    E2E is often a restricted special mode: audio/video only, no browser guests,
                    or available only as a paid add-on.
                  </td>
                </tr>
                <tr>
                  <td valign="top" width="22" style="padding:0 8px 0 0;color:#2563eb;font-size:18px;line-height:1.4;">&#10003;</td>
                  <td style="padding:0;color:#4b5563;font-size:14px;line-height:1.6;">
                    <strong style="color:#111827;">Dedicated hardware in Germany</strong> &ndash;
                    No US cloud and no CLOUD Act access. Mainstream platforms are US-owned,
                    regardless of where their servers are located.
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td align="center" style="padding:24px 32px;background-color:#111827;">
              <p style="margin:0 0 6px 0;color:#d1d5db;font-size:13px;line-height:1.5;">
                Available here
              </p>
              <p style="margin:0;font-size:15px;line-height:1.5;">
                <a href="https://www.dwinity.link" style="color:#ffffff;font-weight:700;text-decoration:none;">
                  www.dwinity.link
                </a>
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</div>`;
/** Write the sample template into /email-templates (first-use helper).
 *  The file is encrypted with the wallet key like every other stored file. */
export async function createSampleInviteTemplate(provider) {
    const file = new File([SAMPLE_TEMPLATE_HTML], SAMPLE_TEMPLATE_NAME, { type: "text/html" });
    await writeFile(provider, EMPTY_STORAGE_PROPS, `${INVITE_TEMPLATE_FOLDER}/${SAMPLE_TEMPLATE_NAME}`, file);
    invalidateInviteTemplateCache();
}
